import { format } from "date-fns";
import { DateObject } from "react-multi-date-picker";
import { buildAppointmentConflictPayload } from "./buildAppointmentConflictPayload";
import {
  checkAppointmentConflicts,
  type AssigneeConflict,
  type CheckAppointmentConflictsResponse,
} from "./checkAppointmentConflicts";

type AssigneeOption = { value: number | string; label: string };

type FormConflictInput = {
  assignee: AssigneeOption[];
  date: string;
  start_time_utc: string;
  end_time_utc: string;
  duration: string;
};

export async function checkFormAppointmentConflicts(
  values: FormConflictInput,
  timezone: string,
  options: {
    excludeAppointmentId?: number | string | null;
    multiDayDates?: DateObject[];
  } = {}
): Promise<CheckAppointmentConflictsResponse> {
  const base = {
    assignee: values.assignee,
    startTimeLocal: values.start_time_utc,
    endTimeLocal: values.end_time_utc,
    duration: values.duration,
    timezone,
    excludeAppointmentId: options.excludeAppointmentId,
  };

  const datesToCheck = [values.date];
  for (const entry of options.multiDayDates ?? []) {
    const d = format(entry.toDate(), "yyyy-MM-dd");
    if (!datesToCheck.includes(d)) datesToCheck.push(d);
  }

  const payloads = datesToCheck
    .map((date) => buildAppointmentConflictPayload({ ...base, date }, date))
    .filter((p) => p != null);

  if (payloads.length === 0) {
    return { conflicts: [], hasConflicts: false };
  }

  const results = await Promise.all(
    payloads.map((payload) => checkAppointmentConflicts(payload!))
  );

  const byAssignee = new Map<number, AssigneeConflict>();
  for (const res of results) {
    for (const group of res.conflicts) {
      const existing = byAssignee.get(group.assigneeId);
      if (!existing) {
        byAssignee.set(group.assigneeId, {
          ...group,
          appointments: [...group.appointments],
        });
        continue;
      }
      const seen = new Set(existing.appointments.map((a) => a.id));
      for (const appt of group.appointments) {
        if (!seen.has(appt.id)) {
          existing.appointments.push(appt);
          seen.add(appt.id);
        }
      }
    }
  }

  const conflicts = Array.from(byAssignee.values());
  return { conflicts, hasConflicts: conflicts.length > 0 };
}
